import { Link } from "react-router-dom";
import { ArrowRight, Home, Search } from "lucide-react";
import { motion } from "framer-motion";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/platform", label: "Platform" },
  { to: "/products/core", label: "MFInsure® Core" },
  { to: "/products/grip", label: "MFInsure® GRIP" },
  { to: "/products/intelligence", label: "Gradatim Intelligence®" },
  { to: "/insights/case-study", label: "Case studies" },
];

export default function NotFoundPage() {
  return (
    <main className="bg-white text-ink min-h-screen flex flex-col" data-testid="not-found-page">
      <SEO
        title="Page not found — Gradatim"
        description="The page you're looking for doesn't exist."
        path="/404"
        noindex
      />
      <Nav />

      <section className="flex-1 flex items-center bg-cream relative overflow-hidden" data-nav-dark={false}>
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.3]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(11,31,58,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(11,31,58,0.05) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.6), transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.6), transparent 80%)",
          }}
        />
        <div className="relative max-w-[840px] mx-auto px-6 md:px-10 py-32 text-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="overline text-copper mb-5">Error 404</div>
            <h1 className="font-display font-medium text-7xl sm:text-8xl md:text-[140px] tracking-tighter leading-none text-ink">
              4<span className="text-copper">0</span>4
            </h1>
            <h2 className="font-display text-2xl md:text-[32px] tracking-tight text-ink mt-8">
              This page doesn't exist.
            </h2>
            <p className="text-[15px] md:text-base text-slate-600 leading-relaxed mt-5 max-w-xl mx-auto">
              The link you followed may be broken, or the page may have been
              moved. Try one of the destinations below.
            </p>

            <div className="mt-10 flex items-center justify-center">
              <Link
                to="/"
                data-testid="not-found-home-btn"
                className="group inline-flex items-center gap-3 bg-ink hover:bg-brand-900 text-white px-6 py-3.5 rounded-md text-sm font-medium tracking-wide transition-colors"
              >
                <Home className="h-4 w-4" strokeWidth={2} />
                Back to home
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="mt-14">
              <div className="flex items-center justify-center gap-3 mb-7">
                <span className="h-px w-12 bg-hairline" />
                <span className="overline text-slate-500">Popular destinations</span>
                <span className="h-px w-12 bg-hairline" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
                {QUICK_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-hairline hover:border-copper/40 hover:shadow-sm transition-all text-[12.5px] font-medium text-ink"
                  >
                    <Search className="h-3 w-3 text-copper" strokeWidth={2} />
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
